/**
 * usePhotoDownload · 选中照片下载控制器（命名弹窗 + 发起导出）· 详情页专用
 *
 *  1. open(photoIDs) · 打开命名弹窗 + 按选中照片的原图地址（file）拉命名候选
 *     （folder / file 两路标题，缺 file 的项跳过，不阻断下载）
 *  2. confirm(namingRule) · 携命名规则发起 exportFolderAndPhotos
 *
 * 副作用归属：埋点由调用方在 open 前自行上报；下载成功后的页面收尾（如退出多选）
 * 通过 onStarted 回调外推，本 hook 只负责数据请求 + 导出 + 弹下载记录抽屉。
 */
import { useCallback, useState } from 'react'
import { message } from 'antd'
import { useAppDispatch } from '../../../app/hooks'
import {
  exportFolderAndPhotos,
  fetchExportTasksV3,
  photoTitles,
  setExportListDrawerVisible
} from '../../../actions/photo'
import * as SentryUtils from '../../../utils/sentryUtils'
import { formatTitlesData } from '../../photoFolders/Detail/lib/photo'
import type { FolderPhotoTitle } from '../components/DownloadModals'

export interface UsePhotoDownloadOptions {
  /** 团队 UUID */
  groupID: string
  /** 当前文件夹 id · 下载 include 的归属 */
  folderID: number
  /** 当前已加载照片 · 取选中项的原图地址拉命名候选（只用到 id / file） */
  photos: ReadonlyArray<{ id: string; file?: string }>
  /** 下载发起成功后的收尾回调（如退出多选）· 由调用方决定 */
  onStarted?: () => void
}

export interface PhotoDownloadController {
  /** 命名弹窗是否打开 */
  visible: boolean
  /** 文件夹级命名候选 · 来自 photoTitles(purpose='folder') */
  folderTitleList: FolderPhotoTitle[]
  /** 照片级命名候选 · 来自 photoTitles(purpose='file') */
  photoTitleList: FolderPhotoTitle[]
  /** 下载请求进行中 · 驱动按钮 loading / disabled */
  downloading: boolean
  /** 打开命名弹窗 + 异步拉取命名候选 */
  open: (photoIDs: string[]) => void
  /** 关闭命名弹窗 */
  close: () => void
  /** 弹窗确认 · 携命名规则发起下载 */
  confirm: (namingRule: string[]) => void
}

/**
 * @param options - 配置参数，含义见 UsePhotoDownloadOptions
 * @returns 照片下载弹窗的状态与操作方法，见 PhotoDownloadController
 */
export function usePhotoDownload({
  groupID,
  folderID,
  photos,
  onStarted
}: UsePhotoDownloadOptions): PhotoDownloadController {
  const dispatch = useAppDispatch()
  const [targetIDs, setTargetIDs] = useState<string[] | null>(null)
  const [folderTitleList, setFolderTitleList] = useState<FolderPhotoTitle[]>([])
  const [photoTitleList, setPhotoTitleList] = useState<FolderPhotoTitle[]>([])
  const [downloading, setDownloading] = useState(false)

  /**
   * 拉取照片下载命名候选 · folder / file 两路各拉一次后用 formatTitlesData 去重合并。
   * 失败软处理：保留空候选（预设命名项仍可用），不阻断弹窗。
   */
  const fetchTitles = useCallback(
    async (files: string[]) => {
      if (!groupID || files.length === 0) return
      const filter = {
        times: [{ start: '', end: '' }],
        include: files,
        exclude: []
      }
      try {
        const [folderResp, fileResp] = await Promise.all([
          Promise.resolve(
            dispatch(
              photoTitles({
                filter,
                groupID,
                purpose: 'folder',
                lastPageCond: ''
              })
            )
          ),
          Promise.resolve(
            dispatch(
              photoTitles({
                filter,
                groupID,
                purpose: 'file',
                lastPageCond: ''
              })
            )
          )
        ])
        const folderTitles =
          (folderResp as { titles?: FolderPhotoTitle[] } | undefined)?.titles ??
          []
        const fileTitles =
          (fileResp as { titles?: FolderPhotoTitle[] } | undefined)?.titles ??
          []
        setFolderTitleList(formatTitlesData(folderTitles))
        setPhotoTitleList(formatTitlesData(fileTitles))
      } catch (error) {
        SentryUtils.capturePromiseException(error)
      }
    },
    [groupID, dispatch]
  )

  const open = useCallback(
    (photoIDs: string[]) => {
      if (photoIDs.length === 0 || !groupID) return
      const idSet = new Set(photoIDs)
      const files = photos
        .filter((p) => idSet.has(p.id) && !!p.file)
        .map((p) => p.file as string)
      setFolderTitleList([])
      setPhotoTitleList([])
      setTargetIDs(photoIDs)
      if (files.length > 0) fetchTitles(files)
    },
    [groupID, photos, fetchTitles]
  )

  const close = useCallback(() => setTargetIDs(null), [])

  const confirm = useCallback(
    (namingRule: string[]) => {
      const ids = targetIDs ?? []
      setTargetIDs(null)
      if (ids.length === 0 || !groupID) return
      setDownloading(true)
      Promise.resolve(
        dispatch(
          exportFolderAndPhotos({
            groupID,
            folderIDs: [],
            include: [{ folderID, photoIDs: ids }],
            namePolicy: { file: namingRule },
            startTime: 0,
            endTime: 0
          })
        )
      )
        .then((result: { status?: number } | undefined) => {
          if (!result || result.status !== 0) {
            message.error('下载请求失败，请稍后重试')
            return
          }
          message.success(`已发起 ${ids.length} 张照片的下载`)
          dispatch(fetchExportTasksV3())
          dispatch(setExportListDrawerVisible(true))
          if (onStarted) onStarted()
        })
        .catch(SentryUtils.capturePromiseException)
        .finally(() => {
          setDownloading(false)
        })
    },
    [targetIDs, groupID, folderID, dispatch, onStarted]
  )

  return {
    visible: !!targetIDs,
    folderTitleList,
    photoTitleList,
    downloading,
    open,
    close,
    confirm
  }
}
